import { Component, inject, signal, computed, CUSTOM_ELEMENTS_SCHEMA, resource } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { lastValueFrom, tap } from 'rxjs';
import { StaffService } from '../../../shared/services/staff.service';
import { AppDataTable, DataColumn, PaginationState } from '../../../shared/components/app-data-table/app-data-table';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AppMetrics, MetricItem } from '../../../shared/components/app-metrics/app-metrics';
import { AppModal } from '../../../shared/components/app-modal/app-modal';
import { AuthService } from '../../../core/services/auth.service';
import { BusinessContextService } from '../../../core/services/business-context.service';
import { CanAccessDirective } from "../../../shared/directives/can-access.directive";
import { AppConfirmDialog } from '../../../shared/components/app-confirm-dialog/app-confirm-dialog';
import { ToastService } from '../../../core/services/toast.service';

@Component({
    selector: 'app-staff-list',
    standalone: true,
    imports: [CommonModule, AppDataTable, AppButton, RouterLink, AppMetrics, AppModal, CanAccessDirective, AppConfirmDialog],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './staff-list.html'
})
export class StaffList {
    private staffService = inject(StaffService);
    public bizCtx = inject(BusinessContextService);
    private authService = inject(AuthService);
    private toast = inject(ToastService);

    // ── Reenvío de Correo (Diálogo) ─────────────────────────────────────────
    showResendConfirm = signal(false);
    userToResend = signal<any | null>(null);
    isResending = signal(false);

    page = signal(1);
    limit = signal(10);
    searchQ = signal('');

    staffResource = resource({
        params: () => ({
            page: this.page(),
            limit: this.limit(),
            search: this.searchQ(),
            business: this.bizCtx.business()
        }),
        loader: async ({ params }) => {
            const { page, limit, search, business } = params;
            // Si no hay business, no podemos cargar personal
            if (!business) {
                this.rawStaff.set([]);
                this.total.set(0);
                return { data: [], total: 0 };
            }

            return lastValueFrom(
                this.staffService.getAll({
                    page,
                    limit,
                    search,
                    business: business // Actualizado a 'business' similar al backend
                }).pipe(
                    tap((response: any) => {
                        const rawData = response.data || [];
                        this.rawStaff.set(rawData.map((item: any) => ({
                            id: item._id,
                            userId: item.user?._id || item.user?.id,
                            fullName: item.user?.fullName || 'Usuario no encontrado',
                            email: item.user?.email || '-',
                            isEmailVerified: item.user?.isEmailVerified === true,
                            isActive: item.isActive,
                            isOdontologist: item.isOdontologist === true,
                            permissionsCount: (item.permissions || []).length,
                            permissions: item.permissions || [],
                            createdAt: item.createdAt || item.user?.createdAt
                        })));
                        this.total.set(response.total || this.rawStaff().length);
                    })
                )
            );
        }
    });

    rawStaff = signal<any[]>([]);
    total = signal(0);
    loading = this.staffResource.isLoading;

    showDetails = signal(false);
    selectedStaff = signal<any | null>(null);

    viewDetails(item: any) {
        this.selectedStaff.set(item);
        this.showDetails.set(true);
    }

    getModulePermissions(item: any): { module: string, actions: string[] }[] {
        const perms = item.permissions || [];
        const grouped: { [key: string]: string[] } = {};

        perms.forEach((p: any) => {
            if (typeof p === 'string') {
                const parts = p.split(':');
                grouped[parts[0]] = grouped[parts[0]] || [];
                grouped[parts[0]].push(parts[1] || 'all');
            }
        });

        return Object.keys(grouped).map(key => ({
            module: key,
            actions: grouped[key]
        }));
    }

    readonly displayStaff = computed(() => this.rawStaff());

    pagination = computed<PaginationState>(() => ({
        current: this.page(),
        limit: this.limit(),
        total: this.total()
    }));

    metricsItems = computed<MetricItem[]>(() => [
        { label: 'Plantilla Activa', value: this.total(), icon: 'solar:users-group-rounded-bold-duotone', variant: 'primary' }
    ]);

    columns: DataColumn[] = [
        { key: 'fullName', title: 'Colaborador', dataIndex: 'fullName' },
        { key: 'email', title: 'Correo', dataIndex: 'email' },
        { key: 'status', title: 'Estado', dataIndex: 'isActive' },
        { key: 'actions', title: '', align: 'right', class: 'w-24' }
    ];

    handleSearch(query: string) {
        this.searchQ.set(query);
        this.page.set(1);
    }

    handlePageChange(page: number) {
        this.page.set(page);
    }

    handleLimitChange(limit: number) {
        this.limit.set(limit);
        this.page.set(1);
    }

    getUserInitials(name: string): string {
        return name?.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() || 'U';
    }

    deleteStaff(id: string) {
        if (confirm('¿Estás seguro de quitar el acceso a este colaborador?')) {
            this.staffService.delete(id).subscribe({
                next: () => this.staffResource.reload(),
                error: (err) => {
                    console.error(err);
                    this.staffResource.reload();
                }
            });
        }
    }

    // ── Resend Verification ──
    canResendVerification = computed(() => {
        return this.isSuperAdmin() || (this.bizCtx.permissions() || []).includes('*');
    });

    isSuperAdmin = computed(() => this.authService.currentUserValue?.isSuperAdmin === true);

    onResendClick(item: any) {
        this.userToResend.set(item);
        this.showResendConfirm.set(true);
    }

    confirmResend() {
        const item = this.userToResend();
        if (!item || !item.userId) return;

        this.isResending.set(true);
        this.authService.resendVerification(item.userId).subscribe({
            next: () => {
                this.showResendConfirm.set(false);
                this.isResending.set(false);
                this.toast.show('Éxito', 'Correo de verificación reenviado con éxito', 'success');
                this.staffResource.reload();
            },
            error: (err: any) => {
                this.showResendConfirm.set(false);
                this.isResending.set(false);
                console.error(err);
                this.toast.show('Error', 'No se pudo enviar el correo: ' + (err.error?.message || err.message), 'error');
            }
        });
    }
}
